import { useEffect, useRef, useState } from 'react';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import { sourceContentRequest } from './common';
import { parseExtractedMarkdown, pasteDocumentContent } from './parseExtractedMarkdown';

export function ExtractedDocument({ assetId, theme: initialTheme }: { assetId: number; theme: 'light' | 'dark' }) {
  const editor = useCreateBlockNote({ trailingBlock: false, pasteHandler: pasteDocumentContent });
  const [theme, setTheme] = useState(initialTheme);
  const [loaded, setLoaded] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);
  const [history, setHistory] = useState<any[] | null>(null);
  const [historical, setHistorical] = useState<number | null>(null);
  const saved = useRef<any[]>([]);
  const busy = useRef(false);
  const parentOrigin = useRef(document.referrer ? new URL(document.referrer).origin : '');
  const notify = (message: any) => {
    if (parentOrigin.current) window.parent.postMessage({ type: 'SOURCE_CONTENT_STATE', assetId, ...message }, parentOrigin.current);
  };
  const load = async (revision?: number) => {
    if (busy.current) return;
    busy.current = true;
    setError('');
    try {
      const data = await sourceContentRequest(assetId, 'GET', null, revision === undefined ? '' : `?version=${revision}`);
      setCanEdit(data.canEdit !== false);
      const blocks = data.body || await parseExtractedMarkdown(editor, data.markdown);
      editor.replaceBlocks(editor.document, blocks.length ? blocks : [{ type: 'paragraph', content: '' }]);
      if (revision === undefined) {
        saved.current = JSON.parse(JSON.stringify(editor.document));
        setVersion(data.version);
        notify({ version: data.version });
      }
      setHistorical(revision ?? null);
      setDirty(false);
      setLoaded(true);
    } catch (e) { setError((e as Error).message); }
    finally { busy.current = false; }
  };
  useEffect(() => { load(); }, [assetId]);
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty) { event.preventDefault(); event.returnValue = ''; }
    };
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent || event.origin !== parentOrigin.current) return;
      if (event.data?.type === 'theme' && ['light', 'dark'].includes(event.data.theme)) {
        setTheme(event.data.theme);
        document.documentElement.setAttribute('data-doc-theme', event.data.theme);
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('message', onMessage);
    notify({ dirty });
    return () => { window.removeEventListener('beforeunload', beforeUnload); window.removeEventListener('message', onMessage); };
  }, [dirty]);
  const save = async () => {
    if (busy.current) return;
    busy.current = true;
    setSaving(true); setError('');
    try {
      const body = JSON.parse(JSON.stringify(editor.document));
      const markdown = await editor.blocksToMarkdownLossy(body);
      const data = await sourceContentRequest(assetId, 'PUT', { body, markdown, version });
      saved.current = body; setVersion(data.version); setEditing(false); setDirty(false);
      setHistory(null);
      notify({ saved: true, version: data.version, dirty: false });
      if (data.processingError) setError(data.processingError);
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); busy.current = false; }
  };
  const showHistory = async () => {
    if (history) { setHistory(null); return; }
    try { setHistory(await sourceContentRequest(assetId, 'GET', null, '/history')); }
    catch (e) { setError((e as Error).message); }
  };
  return <div className="extracted-document">
    <div className="extracted-document__toolbar">
      <span>{historical !== null ? `Revision ${historical}` : version ? 'Edited content' : 'Extracted content'}</span>
      {loaded && canEdit && !editing && historical === null && <button onClick={() => { setEditing(true); setHistory(null); setError(''); }}>Edit</button>}
      {loaded && !editing && <button onClick={showHistory}>History</button>}
      {historical !== null && <button onClick={() => load()}>Back to current</button>}
      {editing && <><button disabled={saving || !dirty} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</button>
        <button disabled={saving} onClick={() => { editor.replaceBlocks(editor.document, saved.current as any); setEditing(false); setDirty(false); setError(''); }}>Cancel</button>
        <span>{dirty ? 'Unsaved changes' : ''}</span></>}
    </div>
    {history && <div className="extracted-document__history">
      {!history.length && <span>No saved revisions yet.</span>}
      {history.map(item => <button key={item.version} onClick={() => load(item.version)}>Revision {item.version} · {new Date(item.createdAt).toLocaleString()}</button>)}
    </div>}
    {error && <p role="alert">{error} {!loaded && <button onClick={() => load()}>Retry loading</button>}</p>}
    {!loaded && !error && <p role="status">Extracting document content…</p>}
    {loaded && <BlockNoteView editor={editor} theme={theme} editable={editing && !saving} onChange={() => { if (editing) setDirty(true); }} />}
  </div>;
}
